import { useState } from "react";
import { Beaker, Triangle, ShoppingBag, FileText } from "lucide-react";

/**
 * Sequence-variant preview — three options for how much sequence
 * data appears on a cloaked card.
 * Public route: /preview/sequence-variants
 */

const SAMPLE = { sku: "BBK-BPC157-5MG", name: "BPC-157", subtitle: "Body Protection Compound", aminoCount: 15, sequencePrefix: "GEPPPGKPA", sequenceFull: "GEPPPGKPADDAGLV" };
const OPTIONS = [
  { key: "A", label: "A · Prefix (current)",         mode: "prefix" },
  { key: "B", label: "B · No sequence line",         mode: "none"   },
  { key: "C", label: "C · Full sequence",            mode: "full"   },
];

const SUPPLY = { text: "#4A0E17", accent: "#C9A24B", ctaBg: "#4A0E17", ctaHover: "#C9A24B" };

export default function PreviewSequenceVariants() {
  return (
    <div className="min-h-screen bg-bone px-6 py-14 lg:py-20" data-testid="seq-variant-page">
      <div className="max-w-[1240px] mx-auto">
        <p className="overline text-oxblood mb-3">Preview · sequence-line options</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight mb-4">How much sequence data on the card?</h1>
        <p className="text-[15px] text-charcoal/80 leading-relaxed max-w-2xl mb-14">
          Hover any card to compare. The rest of the card (name, subtitle, amino
          count, action row, plain-white treatment) is identical across all three.
        </p>

        {OPTIONS.map(opt => (
          <section key={opt.key} className="mb-20">
            <p className="overline text-oxblood mb-3">{opt.label}</p>
            <div className="max-w-[300px]">
              <VariantCard mode={opt.mode} />
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function VariantCard({ mode }) {
  const [reveal, setReveal] = useState(false);
  const t = SUPPLY;
  const p = SAMPLE;
  return (
    <div
      className="relative aspect-square rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(74,14,23,0.35)] cursor-pointer group transition-[border-color,box-shadow] duration-300"
      onMouseEnter={() => setReveal(true)}
      onMouseLeave={() => setReveal(false)}
      data-testid={`variant-${mode}`}
      style={{ borderWidth: 1, borderStyle: "solid", borderColor: reveal ? t.text : `${t.text}33` }}
    >
      <div className="absolute inset-0 bg-white pointer-events-none" aria-hidden="true" />
      <div className="absolute top-3 left-3 z-10">
        <p className="font-serif text-[12px] font-semibold leading-none tracking-tight" style={{ color: t.text }}>
          BulkBuyKits<span className="font-normal">.com</span>
        </p>
        <div className="mt-1.5 h-[2px] w-8 rounded-full" style={{ background: t.accent }} />
      </div>
      <div className="absolute top-3 right-3 z-10">
        <span className="inline-flex items-center rounded-full bg-white/75 backdrop-blur-sm font-mono uppercase tracking-[0.24em] shadow-sm text-[7.5px] px-1.5 py-[3px]" style={{ color: t.text }}>RUO</span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 px-6" style={{ opacity: reveal ? 0 : 1 }}>
        <div className="text-center max-w-[75%]">
          <p className="font-serif text-[22px] leading-[1.05] tracking-tight select-none" style={{ color: t.text, opacity: 0.72 }}>Research</p>
          <p className="font-serif text-[22px] leading-[1.05] tracking-tight select-none" style={{ color: t.text, opacity: 0.72 }}>Peptide</p>
          <p className="font-mono text-[7.5px] mt-3 uppercase tracking-[0.28em]" style={{ color: t.text, opacity: 0.85 }}>Hover to reveal</p>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center pointer-events-none transition-opacity duration-300" style={{ opacity: reveal ? 1 : 0 }}>
        <div className="pl-3 pr-3 max-w-[68%]">
          <h3 className="font-serif font-bold text-[26px] leading-none tracking-tight drop-shadow-sm mb-1.5" style={{ color: t.text }}>{p.name}</h3>
          <p className="font-serif italic text-[10.5px] leading-tight mb-3" style={{ color: t.text, opacity: 0.7 }}>{p.subtitle}</p>
          <div className="space-y-1.5 text-[9.5px]" style={{ color: t.text }}>
            <div className="flex items-center gap-2"><Triangle size={10} strokeWidth={1.8} style={{ color: t.text }} /><span>{p.aminoCount} Amino Acids</span></div>
            {mode === "prefix" && (
              <div className="flex items-center gap-2"><Beaker size={10} strokeWidth={1.8} style={{ color: t.text }} /><span className="font-mono text-[9px]">{p.sequencePrefix}</span></div>
            )}
            {mode === "full" && (
              <div className="flex items-center gap-2"><Beaker size={10} strokeWidth={1.8} style={{ color: t.text }} /><span className="font-mono text-[9px] break-all">{p.sequenceFull}</span></div>
            )}
          </div>
        </div>
      </div>
      <div className="absolute left-3 right-3 bottom-3 z-10 flex items-center gap-1.5 transition-all duration-300" style={{ opacity: reveal ? 1 : 0, pointerEvents: reveal ? "auto" : "none", transform: reveal ? "translateY(0)" : "translateY(8px)" }}>
        <button className="flex-1 inline-flex items-center justify-center rounded-full text-white font-mono uppercase tracking-[0.22em] font-semibold shadow-lg text-[8.5px] px-2 py-1.5 gap-1" style={{ background: t.ctaBg }}>
          <ShoppingBag size={10} strokeWidth={2.5} />Add to Cart
        </button>
        <button className="inline-flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm font-mono uppercase tracking-[0.22em] font-semibold shadow-md text-[8.5px] px-2 py-1.5 gap-1" style={{ color: t.text, borderColor: `${t.text}4D`, borderWidth: 1 }}>
          <FileText size={10} strokeWidth={2.5} />COA
        </button>
      </div>
    </div>
  );
}
